import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { creditsToDollars } from "@/lib/points";

interface CashCreditsState {
  balanceCredits: number;
  dollarBalance: string;
  loading: boolean;
}

/**
 * Realtime read of the user's cash credits wallet (1 credit = $0.01).
 * Cash credits are the redeemable currency — earned only from verified,
 * high-signal events (cashout-trigger milestones, CK postback, etc.).
 */
export function useCashCredits(): CashCreditsState {
  const { user } = useAuth();
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setBalance(0);
      setLoading(false);
      return;
    }

    let channel: ReturnType<typeof supabase.channel> | null = null;

    const fetch = async () => {
      const { data } = await (supabase
        .from("cash_credits" as any)
        .select("balance_credits")
        .eq("user_id", user.id)
        .maybeSingle() as any);
      setBalance((data as { balance_credits?: number } | null)?.balance_credits ?? 0);
      setLoading(false);
    };

    fetch();

    channel = supabase
      .channel(`cash_credits:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "cash_credits",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const row = payload.new as { balance_credits?: number } | null;
          if (row && typeof row.balance_credits === "number") {
            setBalance(row.balance_credits);
          }
        }
      )
      .subscribe();

    return () => {
      if (channel) supabase.removeChannel(channel);
    };
  }, [user]);

  return {
    balanceCredits: balance,
    dollarBalance: creditsToDollars(balance),
    loading,
  };
}